import React, { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { Button, CustomSelect, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@redpanda/ui';
import { DocumentArrowUpIcon, CpuChipIcon } from '@heroicons/react/24/outline';
import { bankReconciliationService } from '../services/api';
import { TableSkeleton } from '@redpanda/ui';
import { toast } from 'react-hot-toast';
import { useBankAccountStore, usePersistentBankAccountStore } from '../stores/bankAccountStore';

interface ImportRecord {
  id: string;
  fileName: string;
  fileSize: number;
  importDate: string;
  totalRecords: number;
  successRecords: number;
  failedRecords: number;
  status: string;
}

const BankReconciliationPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userContext = useContext(UserContext);
  const { isAuthenticated, isLoading: isUserLoading } = userContext!;
  const [importRecords, setImportRecords] = useState<ImportRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);


  const { accounts: bankAccounts, fetchAccounts } = useBankAccountStore();
  const { selectedAccountId, setSelectedAccountId, clearSelectedAccount } = usePersistentBankAccountStore();

  // 頁面載入時清除選擇狀態，確保顯示預設文字
  useEffect(() => {
    clearSelectedAccount();
  }, [clearSelectedAccount]);

  // 載入匯入記錄
  const loadImportRecords = async () => {
    if (!selectedAccountId) return;

    try {
      setLoading(true);
      const response = await bankReconciliationService.getImportRecords({
        bankAccountId: selectedAccountId
      });
      setImportRecords(response.data || []);
    } catch (error) {
      console.error('Failed to load import records:', error);
      toast.error(t('bankReconciliation.toast.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && !isUserLoading) {
      fetchAccounts(1);
    }
  }, [isAuthenticated, isUserLoading, fetchAccounts]);

  useEffect(() => {
    if (selectedAccountId) {
      loadImportRecords();
    }
  }, [selectedAccountId]);

  // 處理檔案上傳
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedAccountId) {
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);
      await bankReconciliationService.importStatement(selectedAccountId, formData);
      toast.success(t('bankReconciliation.toast.importSuccess'));
      loadImportRecords();
    } catch (error: any) {
      console.error('Failed to import statement:', error);
      toast.error(error.response?.data?.message || t('bankReconciliation.toast.importFailed'));
    } finally {
      setUploading(false);
      // 清除檔案選擇
      event.target.value = '';
    }
  };

  // 處理智能比對
  const handleAutoMatch = async () => {
    if (!selectedAccountId) {
      toast.error(t('bankReconciliation.toast.selectAccountFirst'));
      return;
    }

    try {
      setLoading(true);
      const response = await bankReconciliationService.performAutoMatch(selectedAccountId);
      toast.success(t('bankReconciliation.toast.matchSuccess', { count: response.data.autoApplied }));
      loadImportRecords();
    } catch (error: any) {
      console.error('Failed to perform auto match:', error);
      toast.error(t('bankReconciliation.toast.matchFailed'));
    } finally {
      setLoading(false);
    }
  };


  // 格式化檔案大小
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // 格式化狀態
  const formatStatus = (status: string) => {
    const statusMap: Record<string, { text: string; className: string }> = {
      'PROCESSING': { text: t('bankReconciliation.status.PROCESSING'), className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100' },
      'COMPLETED': { text: t('bankReconciliation.status.COMPLETED'), className: 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100' },
      'FAILED': { text: t('bankReconciliation.status.FAILED'), className: 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100' },
      'PARTIAL': { text: t('bankReconciliation.status.PARTIAL'), className: 'bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-100' }
    };
    const statusInfo = statusMap[status] || { text: status, className: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.className}`}>
        {statusInfo.text}
      </span>
    );
  };

  // 處理查看交易記錄
  const handleViewTransactions = () => {
    if (!selectedAccountId) return;

    // 找到選擇的帳戶資料
    const selectedAccount = bankAccounts.find(account => account.id === selectedAccountId);

    navigate(`/bank-accounts/${selectedAccountId}`, {
      state: { account: selectedAccount }
    });
  };


  // 格式化金額


  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      {/* 頁面標題 */}
      <div className="mb-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('bankReconciliation.pageTitle')}</h1>
          {/* 隱藏按鈕保持高度一致 */}
          <div className="opacity-0 pointer-events-none">
            <button className="bg-brand-600 hover:bg-brand-700 text-white font-bold py-2 px-4 rounded transition-all duration-200">
              {t('bankReconciliation.hiddenButton')}
            </button>
          </div>
        </div>
      </div>

      {/* 銀行帳戶選擇 */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6 mb-8">
        <div className="flex items-center">
          <DocumentArrowUpIcon className="h-8 w-8 text-brand-600" />
          <div className="ml-4 flex-1">
            <CustomSelect
              value={selectedAccountId}
              onChange={(value) => setSelectedAccountId(value)}
              className="w-full"
              placeholder={t('bankReconciliation.selectAccount')}
              options={Array.isArray(bankAccounts) ? bankAccounts.map(account => ({
                value: account.id,
                label: `${account.bankName} - ${account.accountName} (${account.accountNumber})`
              })) : []}
            />
          </div>
        </div>
      </div>

      {/* 操作區域 */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6 mb-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('bankReconciliation.statementProcessing')}</h3>
          {selectedAccountId && (
            <button
              onClick={handleViewTransactions}
              className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 text-sm font-medium"
            >
              {t('bankReconciliation.viewTransactions')}
            </button>
          )}
        </div>

        {selectedAccountId ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* 檔案上傳 */}
              <div className="border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg p-6 text-center">
                <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                <div className="mt-4">
                  <div>
                    <span className="mt-2 block text-sm font-medium text-gray-900 dark:text-white">
                      {t('bankReconciliation.uploadStatement')}
                    </span>
                    <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                      {t('bankReconciliation.uploadFormat')}
                    </span>
                    <label
                      htmlFor="file-upload"
                      className={`inline-flex items-center justify-center mt-4 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white cursor-pointer transition-colors ${
                        uploading
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-brand-600 hover:bg-brand-700'
                      }`}
                    >
                      {uploading ? t('bankReconciliation.uploading') : t('bankReconciliation.selectFile')}
                    </label>
                  </div>
                  <input
                    id="file-upload"
                    name="file-upload"
                    type="file"
                    className="sr-only"
                    accept=".csv,.xls,.xlsx"
                    onChange={handleFileUpload}
                    disabled={uploading}
                  />
                </div>
              </div>

              {/* 智能比對 */}
              <div className="border border-gray-300 dark:border-slate-600 rounded-lg p-6 text-center">
                <CpuChipIcon className="mx-auto h-12 w-12 text-blue-500" />
                <div className="mt-4">
                  <span className="block text-sm font-medium text-gray-900 dark:text-white">
                    {t('bankReconciliation.smartMatching')}
                  </span>
                  <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                    {t('bankReconciliation.smartMatchingDesc')}
                  </span>
                  <Button
                    onClick={handleAutoMatch}
                    disabled={loading || !Array.isArray(importRecords) || importRecords.length === 0}
                    className="mt-4 bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {loading ? t('bankReconciliation.matching') : t('bankReconciliation.startMatching')}
                  </Button>
                </div>
              </div>
            </div>

            {/* 使用說明 */}
            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">{t('bankReconciliation.instructions')}</h4>
              <ul className="text-xs text-blue-800 dark:text-blue-200 space-y-1">
                <li>• {t('bankReconciliation.instructionList.supportedBanks')}</li>
                <li>• {t('bankReconciliation.instructionList.autoDetect')}</li>
                <li>• {t('bankReconciliation.instructionList.smartMatch')}</li>
                <li>• {t('bankReconciliation.instructionList.recommendation')}</li>
              </ul>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <DocumentArrowUpIcon className="mx-auto h-16 w-16 text-gray-400" />
            <div className="mt-4">
              <span className="block text-lg font-bold text-gray-900 dark:text-white mb-2">
                {t('bankReconciliation.selectAccount')}
              </span>
              <span className="block text-sm text-gray-400 dark:text-gray-500">
                {t('bankReconciliation.selectAccountToStart')}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* 匯入記錄列表 */}
      <div className="bg-white dark:bg-slate-800 shadow overflow-hidden rounded-md">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('bankReconciliation.importRecords')}</h3>
        </div>

        {!selectedAccountId ? (
          <div className="px-6 py-12 text-center">
            <span className="text-gray-500 dark:text-gray-400">
              {t('bankReconciliation.selectAccountToView')}
            </span>
          </div>
        ) : loading ? (
          <TableSkeleton columns={7} rows={5} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('bankReconciliation.table.fileName')}</TableHead>
                  <TableHead>{t('bankReconciliation.table.fileSize')}</TableHead>
                  <TableHead>{t('bankReconciliation.table.importTime')}</TableHead>
                  <TableHead>{t('bankReconciliation.table.totalRecords')}</TableHead>
                  <TableHead>{t('bankReconciliation.table.successFailed')}</TableHead>
                  <TableHead>{t('bankReconciliation.table.status')}</TableHead>
                  <TableHead>{t('bankReconciliation.table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!Array.isArray(importRecords) || importRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500 dark:text-gray-400">
                    {t('bankReconciliation.noRecords')}
                  </TableCell>
                </TableRow>
              ) : (
                Array.isArray(importRecords) && importRecords.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell className="font-medium">{record.fileName}</TableCell>
                    <TableCell>{formatFileSize(record.fileSize)}</TableCell>
                    <TableCell>
                      {new Date(record.importDate).toLocaleString('zh-TW')}
                    </TableCell>
                    <TableCell>{record.totalRecords}</TableCell>
                    <TableCell>
                      <span className="text-green-600">{record.successRecords}</span>
                      {record.failedRecords > 0 && (
                        <span className="text-red-600"> / {record.failedRecords}</span>
                      )}
                    </TableCell>
                    <TableCell>{formatStatus(record.status)}</TableCell>
                    <TableCell>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="text-brand-600 hover:text-brand-700"
                      >
                        {t('bankReconciliation.viewDetails')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

    </div>
  );
};

export default BankReconciliationPage;